/**
 * 견적서 창 — 도면에서 자동으로 나오는 줄(패널 면적·문) + 부자재·기타 줄, 거래처, 할인 → 저장·인쇄·확정·입금
 * 다시 열 때: 자동 줄은 지금 도면으로 다시 계산(수량), 단가는 저장했던 값이 있으면 그 값 유지 / 부자재·기타 줄은 저장한 그대로
 */
import React, { useEffect, useMemo, useState } from 'react';
import { BankAccount, Customer, PriceData, QuoteHead, fetchPrices, fetchQuote, saveQuote, listCustomers, setJobState, errMsg } from '../utils/api';
import { QuoteLine, AutoInput, autoLines, quoteTotals, printQuote, PrintQuoteOptions } from '../utils/quote';
import { overlay, box, head, xBtn, inp, btn, th, td, num, won, DepositPanel, CustomerForm } from './BizDialogs';
import { C } from './ui';
import { jobOf } from './WorkBoard';

const keyOf = (l: QuoteLine) => `${l.KIND}|${l.SPEC ?? ''}`;
const isAuto = (l: QuoteLine) => l.KIND === 'PANEL' || l.KIND === 'DOOR';
/** 견적서에 찍을 계좌 글자 */
export const accText = (a?: BankAccount | null) => (a ? `${a.BANK_NM} ${a.ACC_NO}${a.HOLDER ? ` (예금주 ${a.HOLDER})` : ''}` : '');

/** 저장된 줄 + 지금 도면의 자동 줄 합치기 */
function merge(saved: QuoteLine[], auto: QuoteLine[], keepCost: boolean): QuoteLine[] {
  const savedCost = new Map(saved.filter(isAuto).map(l => [keyOf(l), l.UNIT_COST]));
  const a = auto.map(l => (keepCost && savedCost.has(keyOf(l)) && savedCost.get(keyOf(l))! > 0 ? { ...l, UNIT_COST: savedCost.get(keyOf(l))! } : l));
  return [...a, ...saved.filter(l => !isAuto(l))];
}

export interface QuoteDialogProps {
  estiNo: string;
  auto?: AutoInput;            // 없으면 수기 견적 (도면 없음 — 자동 줄 없음)
  print: Pick<PrintQuoteOptions, 'siteName' | 'spec'>;
  onClose: () => void;
}
export const QuoteDialog: React.FC<QuoteDialogProps> = ({ estiNo, auto, print, onClose }) => {
  const [prices, setPrices] = useState<PriceData | null>(null);
  const [qh, setQh] = useState<QuoteHead | null>(null);
  const [lines, setLines] = useState<QuoteLine[]>([]);
  const [custs, setCusts] = useState<Customer[]>([]);
  const [custId, setCustId] = useState<number | null>(null);
  const [accId, setAccId] = useState<number | null>(null); // 입금 계좌 (없으면 기본 계좌)
  const [dc, setDc] = useState(0);
  const [memo, setMemo] = useState('');
  const [site, setSite] = useState(print.siteName); // 수기 견적은 여기서 현장명
  const [newCust, setNewCust] = useState(false);
  const [showDepo, setShowDepo] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; t: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const loadHead = () => fetchQuote(estiNo).then(r => { setQh(r.data.head); return r.data; });
  useEffect(() => {
    Promise.all([fetchPrices(), loadHead(), listCustomers()]).then(([p, q, c]) => {
      setPrices(p.data); setCusts(c.data);
      const fresh = auto ? autoLines(auto, p.data) : [];
      setSite(q.head.SITE_NM || '');
      setLines(merge(q.lines, fresh, true));
      setCustId(q.head.CUST_ID); setAccId(q.head.ACC_ID ?? p.data.accounts.find(a => a.DEFAULT_YN === 'Y')?.ACC_ID ?? null); setDc(Number(q.head.DC_AMT) || 0); setMemo(q.head.QUOTE_MEMO || '');
      // 저장한 적 없거나 도면 수량이 바뀌었으면 저장 필요 표시
      setDirty(!q.lines.length || JSON.stringify(q.lines.filter(isAuto).map(l => [keyOf(l), l.QTY])) !== JSON.stringify(fresh.map(l => [keyOf(l), l.QTY])));
    }).catch(e => setMsg({ ok: false, t: errMsg(e) }));
  }, [estiNo]); // eslint-disable-line react-hooks/exhaustive-deps

  const t = useMemo(() => quoteTotals(lines, dc), [lines, dc]);
  const change = (fn: () => void) => { fn(); setDirty(true); setMsg(null); };
  const setLine = (i: number, k: keyof QuoteLine, v: any) => change(() => setLines(ls => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l))));
  const addItem = (id: string) => {
    const it = prices?.items.find(x => String(x.ITEM_ID) === id);
    if (!it) return;
    change(() => setLines(ls => {
      const at = ls.findIndex(l => l.KIND === 'ITEM' && l.ITEM_NM === it.ITEM_NM && (l.SPEC || '') === (it.SPEC || ''));
      if (at >= 0) return ls.map((l, j) => (j === at ? { ...l, QTY: l.QTY + 1 } : l));
      return [...ls, { KIND: 'ITEM', ITEM_NM: it.ITEM_NM, SPEC: it.SPEC, QTY: 1, UNIT: it.UNIT, UNIT_COST: Number(it.UNIT_COST), BIGO: null }];
    }));
  };
  const resetPrices = () => prices && auto && change(() => setLines(ls => merge(ls, autoLines(auto, prices), false)));

  const save = async () => {
    setBusy(true);
    try {
      await saveQuote(estiNo, { custId, accId, lines, dc, memo, ...(qh?.HAND ? { siteName: site } : {}) });
      await loadHead(); setDirty(false); setMsg({ ok: true, t: '견적서를 저장했습니다' });
      return true;
    } catch (e) { setMsg({ ok: false, t: errMsg(e) }); return false; } finally { setBusy(false); }
  };
  const doPrint = async () => {
    const w = window.open('', '_blank'); // 클릭 즉시 열어야 팝업 차단 안 됨
    w?.document.write('<p style="font-family:sans-serif">견적서 만드는 중…</p>');
    if (dirty && !(await save())) { w?.close(); return; }
    const c = custs.find(x => x.CUST_ID === custId) || null, co = prices!.company;
    printQuote({ ...print, siteName: site, estiNo, date: (qh?.QUOTE_DT || new Date().toISOString()).slice(0, 10),
      supplier: { name: co.COMP_NM, bizNo: co.BIZ_NO, ceo: co.CEO_NM, tel: co.TEL, fax: co.FAX, addr: co.ADDR, mgr: qh?.USER_NM ? [qh.USER_NM, qh.USER_TEL].filter(Boolean).join(' ') : co.MGR_NM, email: co.MGR_EMAIL },
      customer: c && { name: c.CUST_NM, bizNo: c.BIZ_NO, ceo: c.CEO_NM, tel: c.TEL || c.HP, fax: c.FAX, addr: c.ADDR, mgr: [c.MGR_NM, c.HP].filter(Boolean).join(' '), email: c.EMAIL },
      lines, dc, memo, terms: co.QUOTE_TERMS || '', staff: qh?.USER_NM || undefined, account: accText(prices!.accounts.find(a => a.ACC_ID === accId)) }, w);
  };
  const confirm = async () => {
    if (dirty && !(await save())) return;
    if (!window.confirm('이 견적을 확정할까요? 확정하면 청구액(공급가액+부가세)이 미수에 잡힙니다.')) return;
    try { await setJobState(estiNo, 'CONFIRMED'); await loadHead(); setMsg({ ok: true, t: '견적을 확정했습니다' }); } catch (e) { setMsg({ ok: false, t: errMsg(e) }); }
  };

  const cell: React.CSSProperties = { ...td, padding: 3 };
  const st = jobOf(qh?.JOB_STATE ?? null);
  return (
    <div style={overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...box, maxWidth: 1000, height: '92vh' }}>
        <div style={head()}>
          <b>{qh?.HAND ? '수기 견적서' : '견적서'} — {estiNo}{site ? ` · ${site}` : ''}</b>
          <span style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ background: C.fill, color: st.color, borderRadius: 999, padding: '5px 12px', fontSize: 13, fontWeight: 700 }}>{st.label}</span>
            <button onClick={onClose} style={xBtn}>✕</button>
          </span>
        </div>
        {!prices || !qh ? <div style={{ padding: 20, color: msg ? '#C62828' : '#888' }}>{msg?.t ?? '불러오는 중…'}</div> : (
          <div style={{ flex: 1, overflow: 'auto', padding: '4px 24px 16px', fontSize: 13 }}>
            {qh.HAND ? <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
              <b>현장명</b><input value={site} onChange={e => change(() => setSite(e.target.value))} placeholder="현장 또는 건 이름" style={{ ...inp, flex: 1, maxWidth: 360 }} />
              <span style={{ fontSize: 12, color: '#8B95A1' }}>도면 없이 품목만 넣는 견적입니다</span>
            </div> : null}
            {/* 거래처 */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
              <b>거래처</b>
              <select value={custId ?? ''} onChange={e => change(() => setCustId(e.target.value ? Number(e.target.value) : null))} style={{ ...inp, minWidth: 220 }}>
                <option value="">(선택 안 함)</option>
                {custs.map(c => <option key={c.CUST_ID} value={c.CUST_ID}>{c.CUST_NM}{c.MGR_NM ? ` · ${c.MGR_NM}` : ''}</option>)}
              </select>
              <button onClick={() => setNewCust(v => !v)} style={btn('#2E7D32', false)}>+ 새 거래처</button>
              <b style={{ marginLeft: 12 }}>입금 계좌</b>
              {prices.accounts.length ? <select value={accId ?? ''} onChange={e => change(() => setAccId(e.target.value ? Number(e.target.value) : null))} style={{ ...inp, minWidth: 220 }}>
                <option value="">(인쇄 안 함)</option>
                {prices.accounts.filter(a => a.USE_YN !== 'N' || a.ACC_ID === accId).map(a => <option key={a.ACC_ID} value={a.ACC_ID}>{accText(a)}{a.DEFAULT_YN === 'Y' ? ' · 기본' : ''}</option>)}
              </select> : <span style={{ fontSize: 12, color: '#8B95A1' }}>없음 — 회사 메뉴 「거래처 · 입금 · 단가」 → 견적서 설정에서 등록</span>}
            </div>
            {newCust && <div style={{ marginBottom: 10 }}><CustomerForm onCancel={() => setNewCust(false)} onSaved={async id => {
              setNewCust(false); const r = await listCustomers(); setCusts(r.data); change(() => setCustId(id));
            }} /></div>}

            {/* 품목 */}
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['품명', '규격', '수량', '단위', '단가', '금액', '비고', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>{lines.map((l, i) => (
                <tr key={i} style={{ background: isAuto(l) ? '#F4F9FF' : undefined }}>
                  <td style={cell}>{isAuto(l) ? <span title="도면에서 자동 계산">🔗 {l.ITEM_NM}</span>
                    : <input value={l.ITEM_NM} onChange={e => setLine(i, 'ITEM_NM', e.target.value)} style={{ ...inp, width: 130 }} />}</td>
                  <td style={cell}>{isAuto(l) ? l.SPEC : <input value={l.SPEC || ''} onChange={e => setLine(i, 'SPEC', e.target.value)} style={{ ...inp, width: 100 }} />}</td>
                  <td style={cell}><input value={l.QTY} onChange={e => setLine(i, 'QTY', Number(e.target.value) || 0)} inputMode="decimal" disabled={l.KIND === 'PANEL'}
                    title={l.KIND === 'PANEL' ? '면적은 도면에서 계산 — 도면을 고치세요' : undefined} style={{ ...inp, width: 72, textAlign: 'right' }} /></td>
                  <td style={cell}>{isAuto(l) ? l.UNIT : <input value={l.UNIT || ''} onChange={e => setLine(i, 'UNIT', e.target.value)} style={{ ...inp, width: 44 }} />}</td>
                  <td style={cell}><input value={won(l.UNIT_COST)} onChange={e => setLine(i, 'UNIT_COST', Number(e.target.value.replace(/,/g, '')) || 0)} inputMode="numeric"
                    style={{ ...inp, width: 96, textAlign: 'right', borderColor: l.UNIT_COST ? 'transparent' : C.red }} /></td>
                  <td style={{ ...num, fontWeight: 'bold' }}>{won(t.amounts[i])}</td>
                  <td style={cell}><input value={l.BIGO || ''} onChange={e => setLine(i, 'BIGO', e.target.value)} style={{ ...inp, width: '100%', minWidth: 120, fontSize: 12 }} /></td>
                  <td style={cell}>{!isAuto(l) && <button onClick={() => change(() => setLines(ls => ls.filter((_, j) => j !== i)))} style={{ ...btn('#C62828', false), padding: '2px 8px', fontSize: 11 }}>빼기</button>}</td>
                </tr>
              ))}</tbody>
            </table>
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <select value="" onChange={e => addItem(e.target.value)} style={{ ...inp, minWidth: 200 }}>
                <option value="">+ 부자재 추가…</option>
                {prices.items.filter(x => x.USE_YN === 'Y').map(x => <option key={x.ITEM_ID} value={x.ITEM_ID}>{x.ITEM_NM}{x.SPEC ? ` ${x.SPEC}` : ''} ({won(x.UNIT_COST)}/{x.UNIT})</option>)}
              </select>
              <button onClick={() => change(() => setLines(ls => [...ls, { KIND: 'ETC', ITEM_NM: '', SPEC: null, QTY: 1, UNIT: '식', UNIT_COST: 0, BIGO: null }]))} style={btn('#555', false)}>+ 기타 줄</button>
              {auto && <button onClick={resetPrices} title="패널·문 단가를 지금 단가표 값으로" style={btn('#555', false)}>단가표 값으로 되돌리기</button>}
              {auto && <span style={{ fontSize: 11, color: '#888' }}>🔗 줄은 도면에서 자동 계산 (면적 = 벽 둘레×높이 + 천장 + 바닥 + 칸막이)</span>}
            </div>

            {/* 합계 */}
            <div style={{ display: 'flex', gap: 16, marginTop: 14, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <label style={{ flex: 1, minWidth: 260, fontSize: 12, color: '#555' }}>비고 (견적서에 인쇄)
                <textarea value={memo} onChange={e => change(() => setMemo(e.target.value))} rows={4} style={{ ...inp, width: '100%', fontFamily: 'inherit', marginTop: 2 }} /></label>
              <table style={{ borderCollapse: 'collapse', minWidth: 300 }}><tbody>
                <tr><td style={td}>합계</td><td style={num}>{won(t.sum)}</td></tr>
                <tr><td style={td}>할인</td><td style={{ ...num, padding: 3 }}><input value={won(dc)} onChange={e => change(() => setDc(Number(e.target.value.replace(/,/g, '')) || 0))} inputMode="numeric" style={{ ...inp, width: 110, textAlign: 'right' }} /></td></tr>
                <tr><td style={td}>공급가액</td><td style={num}>{won(t.supply)}</td></tr>
                <tr><td style={td}>부가세 10%</td><td style={num}>{won(t.vat)}</td></tr>
                <tr><td style={{ ...td, fontWeight: 'bold' }}>총 합계</td><td style={{ ...num, fontWeight: 'bold', fontSize: 16, color: C.blue }}>{won(t.total)}</td></tr>
              </tbody></table>
            </div>

            {/* 입금 */}
            <div style={{ marginTop: 12 }}>
              <button onClick={() => setShowDepo(v => !v)} style={btn('#2E7D32', false)}>{showDepo ? '입금 닫기' : `💰 입금 (입금 ${won(qh.PAID)})`}</button>
              {showDepo && <div style={{ marginTop: 8 }}><DepositPanel estiNo={estiNo} billed={(Number(qh.TOTAL_AMT) || 0) + (Number(qh.VAT_AMT) || 0)} onChanged={loadHead} /></div>}
            </div>
          </div>
        )}
        <div style={{ borderTop: `1px solid ${C.fill}`, padding: '14px 24px 18px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {msg && <span style={{ fontSize: 13, color: msg.ok ? C.green : C.red }}>{msg.ok ? '✓' : '⚠'} {msg.t}</span>}
          {dirty && !msg && <span style={{ fontSize: 13, color: C.orange }}>● 저장 안 된 변경</span>}
          <span style={{ marginLeft: 'auto' }} />
          {qh && !qh.JOB_STATE && <button onClick={confirm} disabled={busy} style={btn('#1976D2', false)}>견적 확정</button>}
          <button onClick={doPrint} disabled={busy || !prices} style={btn(C.blue, false)}>🖨 인쇄</button>
          <button onClick={save} disabled={busy || !prices} style={btn(C.blue)}>{busy ? '저장 중…' : '💾 저장'}</button>
        </div>
      </div>
    </div>
  );
};
